import React, { MutableRefObject, useEffect, useId, useRef } from 'react';
import { useDropzone } from 'react-dropzone';
import styled from 'styled-components';

import { CourseAssignmentFileCard } from '@/features/course/learner/atoms/CourseAssignmentFileCard';
import { ASSIGNMENT_FILE_ACCEPT, ASSIGNMENT_FILE_MAX_SIZE_BYTES } from '@/features/course/learner/constants/assignment';
import { AssignmentFile } from '@/features/course/learner/types/assignment';
import { Button } from '@/ui/Button';
import { DropArea, FileRejection } from '@/ui/DropArea';
import { HighlightPulseEffect } from '@/ui/Effects/HighlightPulseEffect';
import { Icon } from '@/ui/Icon';
import { IndeterminateCircularProgressBar } from '@/ui/ProgressBar';

const RootStyled = styled.section`
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
  padding: 32px 32px 48px;
  border: 1px solid ${(p) => p.theme.colors.neutral.outline1};
  border-radius: 12px;
  background-color: ${(p) => p.theme.colors.primary.onMain};
`;

/* The legacy global stylesheet still puts em-based margins on headings and paragraphs */
const OverlineStyled = styled.h2`
  ${(p) => p.theme.typography.overline};
  margin: 0;
  padding-bottom: 8px;
  text-transform: uppercase;
  color: ${(p) => p.theme.colors.neutral.onBackground};
`;

const DropAreaIconStyled = styled(Icon)`
  font-size: 24px;
  color: ${(p) => p.theme.colors.neutral.outline2};
`;

const DropAreaHintStyled = styled.p`
  ${(p) => p.theme.typography.caption};
  margin: 8px 0 0;
  color: ${(p) => p.theme.colors.neutral.onContainer};
`;

const DropAreaPromptStyled = styled.p`
  ${(p) => p.theme.typography.body2};
  margin: 16px 0 0;
  color: ${(p) => p.theme.colors.neutral.onBackground};
`;

/**
 * Looks like the link it stands in for. It cannot be one: the drop area is itself the
 * control, so a nested button or anchor would be unreachable.
 */
const BrowseStyled = styled.span`
  color: ${(p) => p.theme.colors.primary.main};
`;

/** Takes the drop area's place and size while a file uploads, so the box does not jump. */
const UploadingStyled = styled.div`
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  width: 100%;
  min-height: 197px;
  padding: 50px 34px;
  border: 2px dashed ${(p) => p.theme.colors.neutral.outline1};
  border-radius: 8px;
  background-color: ${(p) => p.theme.tones.neutral[99]};
`;

const UploadingLabelStyled = styled.p`
  ${(p) => p.theme.typography.body2};
  margin: 0;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: ${(p) => p.theme.colors.neutral.onBackground};
`;

const SubmitRowStyled = styled.div`
  display: flex;
  justify-content: flex-end;
`;

type CourseAssignmentSubmissionProps = {
  /** The uploaded file — submitted when `submittedAt` is set, a draft otherwise. */
  file: AssignmentFile | null;
  submittedAt?: Date;
  /** Name of the file being uploaded. Replaces the box content with a loading state. */
  uploadingFileName: string | null;
  submitting: boolean;
  onFileSelected: (file: File) => void;
  /** Wrong type or over the size limit, from a drop or the picker. The box shows nothing itself. */
  onFilesRejected: (rejections: FileRejection[]) => void;
  onRemove: VoidFunction;
  onSubmit: VoidFunction;
  /** The empty drop area, for the course navigation to focus and open. */
  dropAreaRef: MutableRefObject<HTMLButtonElement | null>;
  /** Receives the trigger for the Submit button's highlight pulse. */
  setSubmitHighlightCallback: (highlight: VoidFunction) => void;
  className?: string;
};

/**
 * "Your submission" on an assignment activity: an empty PDF drop area, the upload in progress,
 * or the uploaded file with its actions — and Submit while that file is still a draft.
 */
export const CourseAssignmentSubmission = ({
  file,
  submittedAt,
  uploadingFileName,
  submitting,
  onFileSelected,
  onFilesRejected,
  onRemove,
  onSubmit,
  dropAreaRef,
  setSubmitHighlightCallback,
  className,
}: CourseAssignmentSubmissionProps): JSX.Element => {
  // Replace opens the same PDF-only picker as the drop area, with the same rejections, but
  // from a button on the file card — so it needs a picker of its own, without a drop target.
  const { getInputProps: getReplaceInputProps, open: openReplacePicker } = useDropzone({
    accept: ASSIGNMENT_FILE_ACCEPT,
    maxSize: ASSIGNMENT_FILE_MAX_SIZE_BYTES,
    multiple: false,
    noClick: true,
    noKeyboard: true,
    noDrag: true,
    onDrop: ([acceptedFile], fileRejections) => {
      if (acceptedFile) onFileSelected(acceptedFile);

      if (fileRejections.length) onFilesRejected(fileRejections);
    },
  });

  // Removing the file unmounts the button that had focus. Keyboard users land on the drop area
  // that replaces it, rather than at the top of the document.
  const focusDropAreaOnMountRef = useRef(false);
  const titleId = useId();

  useEffect(() => {
    if (!file && !uploadingFileName && focusDropAreaOnMountRef.current) {
      focusDropAreaOnMountRef.current = false;
      dropAreaRef.current?.focus();
    }
  }, [file, uploadingFileName, dropAreaRef]);

  const handleRemove = () => {
    focusDropAreaOnMountRef.current = true;
    onRemove();
  };

  const renderContent = () => {
    if (uploadingFileName) {
      return (
        <UploadingStyled role="status">
          <IndeterminateCircularProgressBar size={32} />
          <UploadingLabelStyled title={uploadingFileName}>{`Uploading ${uploadingFileName}`}</UploadingLabelStyled>
        </UploadingStyled>
      );
    }

    if (file) {
      return (
        <CourseAssignmentFileCard
          fileName={file.name}
          fileUrl={file.url}
          submittedAt={submittedAt}
          onReplace={openReplacePicker}
          onRemove={handleRemove}
          actionsDisabled={submitting}
        />
      );
    }

    return (
      <DropArea
        accept={ASSIGNMENT_FILE_ACCEPT}
        maxSize={ASSIGNMENT_FILE_MAX_SIZE_BYTES}
        onFilesSelected={([selectedFile]) => onFileSelected(selectedFile)}
        onFilesRejected={onFilesRejected}
        aria-label="Upload a PDF file"
        buttonRef={dropAreaRef}
      >
        <DropAreaIconStyled icon={['far', 'cloud-arrow-up']} aria-hidden />
        <DropAreaHintStyled>.pdf files only</DropAreaHintStyled>
        <DropAreaPromptStyled>
          Drop your file here or <BrowseStyled>browse files</BrowseStyled>
        </DropAreaPromptStyled>
      </DropArea>
    );
  };

  return (
    <RootStyled className={className} aria-labelledby={titleId}>
      <OverlineStyled id={titleId}>Your submission</OverlineStyled>
      {renderContent()}
      <input {...getReplaceInputProps()} />
      {!!file && !submittedAt && !uploadingFileName && (
        <SubmitRowStyled>
          <HighlightPulseEffect setHighlightedCallback={setSubmitHighlightCallback}>
            <Button variant="primary" size="large" onClick={onSubmit} $loading={submitting}>
              Submit
            </Button>
          </HighlightPulseEffect>
        </SubmitRowStyled>
      )}
    </RootStyled>
  );
};
